import { surveryCampaignDefinitions, SurveyStringMap } from './surveySettingsAndConstants';
import { FloodgateSettingDataType } from '../services/constants';
import deleteSettings from '../services/deleteSettings';
import readSettings from '../services/readSettings';
import upsertSettings from '../services/upsertSettings';
import { getGuid } from 'owa-guid';
import getPackageBaseUrl from 'owa-session-store/lib/actions/getPackageBaseUrl';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getPalette } from 'owa-theme';
import { trace } from 'owa-trace';
import { hasQueryStringParameter, isGulpOrBranching } from 'owa-querystring';

require('../resources/custom_officebrowserfeedback.scss');

const CONSUMER_GUID = '00000000-0000-0000-0000-000000000000';
const CampaignStates = 'CampaignStates';
const GovernedChannelStates = 'GovernedChannelStates';
const SurveyActivationStats = 'SurveyActivationStats';

interface FloodgateSetting {
    // A collection settings
    [key: string]: string;
}

const SettingsMap: { [key: string]: FloodgateSetting } = {};

// used for debugging and testing without the roaming settings from SDS
let bypassRoamingSettings = true;

function loadFloodgateScript(): Promise<any> {
    return isGulpOrBranching() ? import('./importFloodgate') : import('./importFloodgateMinified');
}

/**
 * Loads and initialize the floodgate engine. If the engine is loaded/initalized, it will just return
 * For documentation on the InitOptions, please refer to:
 * https://microsoft.sharepoint.com/teams/Office_CLE/officecustomervoice/SitePages/Office%20Feedback%20SDK%20-%20Web%20-%20Floodgate.aspx
 */
export default async function loadAndInitializeFloodgateEngine(searchTraceId: string) {
    if (!hasQueryStringParameter('bpfloodgatesettings')) {
        // Fetch settings to pass to floodgate engine
        SettingsMap[CampaignStates] = await readSettings(CampaignStates);
        SettingsMap[GovernedChannelStates] = await readSettings(GovernedChannelStates);
        SettingsMap[SurveyActivationStats] = await readSettings(SurveyActivationStats);
        bypassRoamingSettings = false;
    }

    if (window.OfficeBrowserFeedback) {
        // In case this is initialized and not started, just restart
        // If the engine is already started, this is a no-op on their code
        startFloodgateEngine();
        return;
    }

    try {
        // Load the script
        await loadFloodgateScript();

        const userConfig = getUserConfiguration();
        const palette = getPalette();
        const isTest = isGulpOrBranching();

        OfficeBrowserFeedback.initOptions = {
            appId: 2222,
            stylesUrl: '#',
            intlUrl: `${getPackageBaseUrl()}resources/officebrowserfeedback/intl/`,
            environment: isTest ? 1 : 0, // 0 - Prod, 1 - Int
            primaryColour: palette.themePrimary,
            secondaryColour: palette.headerButtonsBackgroundHover,
            locale: userConfig.SessionSettings.UserCulture,
            sessionId: getGuid(),
            telemetryGroup: {
                tenantId: isConsumer() ? CONSUMER_GUID : userConfig.SessionSettings.TenantGuid,
                loggableUserId: userConfig.SessionSettings.UserPuid,
            },
            applicationGroup: {
                appData: JSON.stringify({
                    searchTraceId: searchTraceId,
                }),
                feedbackTenant: userConfig.SessionSettings.TenantDomain,
            },
            onError: (err: string) => {
                trace.error('Floodgate SDK could not be initialized. Error: ' + err);
            },
        };

        OfficeBrowserFeedback.floodgate.initOptions = {
            autoDismiss: 4, // 28s
            campaignDefinitions: surveryCampaignDefinitions,
            uIStringGetter: getLocString,
            settingStorageCallback: {
                readSettingList: readSettingsList,
                deleteSettingItem: deleteSettingItem,
                upsertSettingItem: upsertSettingItem,
            },
        };

        await window.OfficeBrowserFeedback.floodgate.initialize();

        await window.OfficeBrowserFeedback.floodgate.start();

        addWindowEventListeners();
    } catch (error) {
        trace.error('Failed to load floodgate javascript: ' + error);
    }
}

function readSettingsList(settingName: string) {
    // Always read from the local/cached settings
    return SettingsMap[settingName];
}

function upsertSettingItem(setting: string, itemKey: string, itemValue: string) {
    if (bypassRoamingSettings) {
        return;
    }

    // Update local/cached settings
    if (!SettingsMap[setting]) {
        SettingsMap[setting] = {};
    }

    SettingsMap[setting][itemKey] = itemValue;

    const request: FloodgateSettingDataType = {
        SettingName: setting,
        ItemKey: itemKey,
        ItemValue: itemValue,
    };

    // Upsert the settings on the server
    upsertSettings(request);
}

function deleteSettingItem(setting: string, itemKey: string) {
    if (bypassRoamingSettings) {
        return;
    }

    // Update local/cached settings
    SettingsMap[setting][itemKey] = null;
    const request: FloodgateSettingDataType = {
        SettingName: setting,
        ItemKey: itemKey,
    };

    // Delete the settings on the server
    deleteSettings(request);
}

function getLocString(placeholderString: string): string {
    return SurveyStringMap[placeholderString]();
}

function addWindowEventListeners() {
    window.addEventListener('focus', startFloodgateEngine);
    window.addEventListener('blur', stopFloodgateEngine);
    window.addEventListener('unload', stopFloodgateEngine);
}

function startFloodgateEngine() {
    if (window.OfficeBrowserFeedback.floodgate) {
        window.OfficeBrowserFeedback.floodgate.start();
    }
}

function stopFloodgateEngine() {
    if (window.OfficeBrowserFeedback.floodgate) {
        window.OfficeBrowserFeedback.floodgate.stop();
    }
}
